import { ConditionOperatorEnumType } from '@reach/shared-types/conditions';
import React, { useState } from 'react';
import styles from './FieldsAndOperatorsPanel.module.css';

const AVAILABLE_OPERATORS = Object.values(ConditionOperatorEnumType);

interface FieldsAndOperatorsPanelProps {
  availableFields: string[];
  isLoadingFields: boolean;
  fieldsError?: Error | null;
}

interface TooltipPosition {
  top: number;
  left: number;
}

function TagList({
  items,
  variant = 'default',
}: {
  items: string[];
  variant?: 'default' | 'operators';
}): React.ReactElement {
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);

  const handleCopy = async (
    e: React.MouseEvent<HTMLButtonElement>,
    text: string,
  ) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({
      top: rect.bottom + 4,
      left: rect.left + rect.width / 2,
    });
    await navigator.clipboard.writeText(text);
    setCopiedItem(text);
    setTimeout(() => {
      setCopiedItem(null);
      setTooltipPos(null);
    }, 1500);
  };

  const className =
    variant === 'operators'
      ? `${styles.tagList} ${styles.operators}`
      : styles.tagList;

  return (
    <ul className={className}>
      {items.map(item => (
        <li key={item}>
          <button type="button" onClick={e => handleCopy(e, item)}>
            {item}
          </button>
        </li>
      ))}
      {copiedItem && tooltipPos && (
        <span
          className={styles.copiedTooltip}
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: 'translateX(-50%)',
          }}>
          Copied!
        </span>
      )}
    </ul>
  );
}

function FieldsSection({
  fields,
  isLoading,
  error,
}: {
  fields: string[];
  isLoading: boolean;
  error?: Error | null;
}): React.ReactElement {
  if (isLoading) {
    return <p>Loading fields...</p>;
  }

  if (error) {
    return <p className={styles.error}>Failed to load fields.</p>;
  }

  if (fields.length === 0) {
    return <p>No custom fields configured.</p>;
  }

  return <TagList items={fields} />;
}

export const FieldsAndOperatorsPanel: React.FC<
  FieldsAndOperatorsPanelProps
> = ({ availableFields, isLoadingFields, fieldsError }) => {
  return (
    <details className={styles.panel}>
      <summary>View available attributes and operators</summary>
      <section>
        <p className={styles.hint}>Click any item to copy</p>

        <div>
          <h4>Attributes:</h4>
          <FieldsSection
            fields={availableFields}
            isLoading={isLoadingFields}
            error={fieldsError}
          />
        </div>

        <div>
          <h4>Operators:</h4>
          <TagList items={AVAILABLE_OPERATORS} variant="operators" />
        </div>
      </section>
    </details>
  );
};
